import { useState, useCallback } from 'react';
import { useCurrenciesStore } from '../stores/currenciesStore';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

/**
 * A custom hook for managing currency selection and handling currency change operations
 * 
 * @returns {Object} An object containing currency-related state and operations
 */
export const useCurrencyManager = () => {
  const { t } = useTranslation();
  const { 
    currencies, 
    selectedCurrency, 
    loading, 
    error, 
    setSelectedCurrency 
  } = useCurrenciesStore(state => state);
  
  const [isChanging, setIsChanging] = useState(false);
  
  /**
   * Change the application's currency with proper error handling
   * 
   * @param {string} currency - The currency code to change to
   * @returns {Promise<boolean>} A promise that resolves to true if successful
   */
  const changeCurrency = useCallback(async (currency) => {
    if (currency === selectedCurrency || isChanging) {
      return false;
    }
    
    setIsChanging(true);
    try {
      await setSelectedCurrency(currency);
      toast.success(t('currency.changed_success', 'Currency changed successfully'));
      return true;
    } catch (error) {
      console.error('Failed to change currency:', error);
      toast.error(t('currency.changed_error', 'Failed to change currency'));
      return false;
    } finally {
      setIsChanging(false);
    }
  }, [selectedCurrency, isChanging, setSelectedCurrency, t]);
  
  /**
   * Get the formatted display name of a currency
   * 
   * @param {string} currencyCode - The currency code to get info for
   * @returns {string} The formatted currency name
   */
  const getCurrencyDisplay = useCallback((currencyCode) => {
    const currency = currencies.find(c => c.short_name === currencyCode);
    if (!currency) return currencyCode;
    return currency.name ? `${currency.short_name} (${currency.name})` : currency.short_name;
  }, [currencies]);
  
  return {
    currencies,
    selectedCurrency,
    loading: loading || isChanging,
    error,
    changeCurrency,
    getCurrencyDisplay
  };
};

export default useCurrencyManager; 